// The live-preview iframe: owns the frozen postMessage handshake with
// frontend/display's preview mode (frontend/display/src/preview.ts) and the
// scale-to-fit presentation of a fixed-size iframe. Deliberately takes an
// already-built `DisplayValue` rather than a template/screen pair — resolving
// those into a draft (draft.ts) and resolving asset URLs lives one layer up,
// in preview-panel.tsx, which keeps this component context-free and
// synchronously testable with a fake draft.
//
// Protocol (frozen by plan 05a, mirrored from frontend/display/src/preview.ts):
//   iframe -> parent: {type: "ewt-preview-ready"}   (on load, and again on
//                                                     every reload)
//   parent -> iframe: {type: "ewt-preview", value}  (targetOrigin "*" — the
//                                                     payload only drives
//                                                     rendering inside an
//                                                     iframe we already
//                                                     control, so an origin
//                                                     check would add config,
//                                                     not security; matches
//                                                     the display page's own
//                                                     side of this contract)
// A (re-)ready message gets an immediate, undebounced reply — a reloaded
// iframe has no draft to show until this arrives, so waiting the usual
// 150ms would flash it blank. Every other draft change is debounced 150ms so
// a fast typist in the value stepper doesn't flood the iframe with posts.

import { MoveDiagonal2 } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";
import type { DisplayValue } from "./display-contract";
import type { PreviewSize } from "./preview-controls";

const POST_DEBOUNCE_MS = 150;
const MIN_SIZE = 1;

function isReadyMessage(data: unknown): boolean {
    return typeof data === "object" && data !== null && (data as { type?: unknown }).type === "ewt-preview-ready";
}

export interface PreviewFrameProps {
    value: DisplayValue;
    width: number;
    height: number;
    /** Wired to the corner drag handle. Omitted, the frame still renders at
     *  the requested size — only dragging to resize is unavailable. */
    onSizeChange?: (size: PreviewSize) => void;
}

export function PreviewFrame({ value, width, height, onSizeChange }: PreviewFrameProps) {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Latest-value ref: the ready handler (registered once, deps: []) needs
    // the current draft even though it isn't itself a dependency of that
    // effect — see the debounce effect below for why `value` can't just be
    // added to the ready-listener's deps instead.
    const valueRef = useRef(value);
    valueRef.current = value;
    const readyRef = useRef(false);
    const [ready, setReady] = useState(false);

    // Registered once: re-subscribing on every draft change would risk
    // missing a message during the gap between removing the old listener and
    // attaching the new one.
    useEffect(() => {
        function onMessage(event: MessageEvent) {
            if (event.source !== iframeRef.current?.contentWindow) return;
            if (!isReadyMessage(event.data)) return;
            readyRef.current = true;
            setReady(true);
            iframeRef.current?.contentWindow?.postMessage({ type: "ewt-preview", value: valueRef.current }, "*");
        }
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, []);

    // Debounced post on every draft change. Deliberately keyed on `value`
    // alone (not `ready`) — `readyRef` is read instead of `ready` state so
    // this effect does not also fire the moment the ready listener above
    // flips `ready` to true, which would race the immediate post it just
    // sent with a redundant debounced one 150ms later.
    useEffect(() => {
        if (!readyRef.current) return;
        const timer = setTimeout(() => {
            iframeRef.current?.contentWindow?.postMessage({ type: "ewt-preview", value }, "*");
        }, POST_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [value]);

    // Scale-to-fit: the iframe itself stays a fixed size (the requested true
    // pixel dimensions, so the display page lays out exactly as it would on
    // real signage) and a CSS transform shrinks it to the panel's actual
    // width; the wrapper's height is set to match so the scaled-down iframe
    // doesn't leave a tall gap of empty space below it.
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
        const node = containerRef.current;
        if (!node || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setContainerWidth(entry.contentRect.width);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const scale = containerWidth > 0 ? Math.min(1, containerWidth / width) : 1;
    // The drag handle reads this instead of `scale` directly: a pointermove
    // handler set up once (per pointer-down) still needs the freshest scale,
    // since resizing itself changes `scale` mid-drag.
    const scaleRef = useRef(scale);
    scaleRef.current = scale;

    const dragStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

    function handleResizePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
        if (!onSizeChange) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStartRef.current = { x: event.clientX, y: event.clientY, width, height };
    }

    function handleResizePointerMove(event: ReactPointerEvent<HTMLButtonElement>) {
        const start = dragStartRef.current;
        if (!start || !onSizeChange) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        // Divide by the current scale: the iframe is transformed down to fit
        // the panel, so a pixel of pointer travel on screen is more than a
        // pixel of device size.
        const currentScale = scaleRef.current || 1;
        onSizeChange({
            width: Math.max(MIN_SIZE, Math.round(start.width + dx / currentScale)),
            height: Math.max(MIN_SIZE, Math.round(start.height + dy / currentScale)),
        });
    }

    function handleResizePointerUp(event: ReactPointerEvent<HTMLButtonElement>) {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        dragStartRef.current = null;
    }

    return (
        <div className="flex flex-col gap-2">
            <div
                ref={containerRef}
                className="relative w-full overflow-hidden rounded-kore-md border border-kore-border bg-kore-surface-sunken"
            >
                <div style={{ height: height * scale }}>
                    <iframe
                        ref={iframeRef}
                        title="Live preview"
                        src="/d/preview"
                        width={width}
                        height={height}
                        style={{ border: "none", transform: `scale(${scale})`, transformOrigin: "top left" }}
                    />
                </div>
                <button
                    type="button"
                    aria-label="Resize preview"
                    onPointerDown={handleResizePointerDown}
                    onPointerMove={handleResizePointerMove}
                    onPointerUp={handleResizePointerUp}
                    className="absolute bottom-1 right-1 flex size-6 cursor-nwse-resize items-center justify-center rounded border-0 bg-kore-surface text-muted-foreground shadow"
                >
                    <MoveDiagonal2 className="size-3.5" aria-hidden />
                </button>
            </div>
            {!ready && <p className="text-xs text-muted-foreground">Connecting to the preview…</p>}
        </div>
    );
}
